import { APITypesV1 } from "@cur8/api-client";
import { Button, Icon24ArrowRight, Typography } from "@cur8/maneki";
import { Room, Visit } from "@cur8/rich-entity";
import { usePatientQuery } from "hooks/queries/usePatientQuery";
import { useRoomQueries } from "hooks/queries/useRoomQuery";
import { useTodaysScheduleQuery } from "hooks/queries/useTodaysScheduleQuery";
import { useVisitHistoryQuery } from "hooks/queries/useVisitHistoryQuery";
import { useVisitQueries } from "hooks/queries/useVisitQuery";
import { generateVisitTypeLabel } from "lib/generateVisitTypeLabel";
import { getAndParseLocationCookie } from "lib/nekoCookies";
import { useMemo, useState } from "react";
import { useFeaturePreference } from "render/hooks/useFeaturePreference";
import { getScanFlowUrl } from "render/routes/getScanFlowUrl";
import { paths } from "render/routes/paths";
import { ActionsFooter } from "render/ui/layout/ActionsFooter";
import { InformationLayout } from "render/ui/layout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import { RadioGroup } from "render/ui/presentation/RadioGroup";
import styles from "./styles.module.sass";

export function SelectActiveVisit({
  goTo,
  onBack,
}: {
  goTo: (url: string) => void;
  onBack(): void;
}) {
  const location = getAndParseLocationCookie();
  const siteId = location?.siteId;

  const scheduleQuery = useTodaysScheduleQuery({ siteId });
  const visitsData = useVisitQueries(scheduleQuery.data);
  const activeVisits = visitsData
    .filter((visit) => visit.data != null)
    .map((visit) => visit.data)
    .filter(
      (visit) =>
        visit.status === APITypesV1.VisitStatus.CheckedIn ||
        visit.status === APITypesV1.VisitStatus.Ongoing ||
        visit.status === APITypesV1.VisitStatus.Completed
    )
    .sort((a, b) => {
      if (a == null) {
        return 1;
      }
      if (b == null) {
        return -1;
      }
      return a.startTime < b.startTime ? -1 : 1;
    });
  const checkedInVisits = activeVisits.filter(
    (visit) => visit.status === APITypesV1.VisitStatus.CheckedIn
  );
  const ongoingAndCompletedVisits = activeVisits.filter(
    (visit) =>
      visit.status === APITypesV1.VisitStatus.Ongoing ||
      visit.status === APITypesV1.VisitStatus.Completed
  );
  const [selectedVisitId, setSelectedVisitId] = useState<string | undefined>(
    undefined
  );
  const roomIds = activeVisits.map((visit) => visit.roomId);
  const roomQueries = useRoomQueries(roomIds);
  const rooms = roomQueries
    .filter((room) => room.data != null)
    .map((room) => room.data);

  const selectedVisit = useMemo(() => {
    return activeVisits.find((visit) => visit.visitId === selectedVisitId);
  }, [selectedVisitId, activeVisits]);

  const { data: featureFlags } = useFeaturePreference();

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout}>
            <div className={styles.title}>
              <Typography variant="display-s">Active sessions</Typography>
              <Typography variant="body-m" color="subtle">
                Select the member whose session you'd like to restore.
              </Typography>
            </div>
            <RadioGroup
              aria-label="select visit"
              onChange={(value: string) => setSelectedVisitId(value)}
            >
              <div className={styles.radioGroup}>
                <div className={styles.radios}>
                  <Typography variant="title-m">Checked-in</Typography>
                  {checkedInVisits.length > 0 ? (
                    checkedInVisits.map((visit) => (
                      <VisitRadio
                        key={visit.visitId}
                        visit={visit}
                        rooms={rooms}
                      />
                    ))
                  ) : (
                    <Typography variant="body-m">
                      No checked-in visits
                    </Typography>
                  )}
                </div>
                <div className={styles.radios}>
                  <Typography variant="title-m">
                    Ongoing and completed visits
                  </Typography>
                  {ongoingAndCompletedVisits.length > 0 ? (
                    ongoingAndCompletedVisits.map((visit) => (
                      <VisitRadio
                        key={visit.visitId}
                        visit={visit}
                        rooms={rooms}
                      />
                    ))
                  ) : (
                    <Typography variant="body-m">
                      No ongoing or completed
                    </Typography>
                  )}
                </div>
              </div>
            </RadioGroup>
          </section>
        }
        buttons={
          <ActionsFooter
            left={
              <Button variant="secondary" onPress={onBack} fillContainer>
                Back
              </Button>
            }
            right={
              <Button
                state={!selectedVisit ? "disabled" : undefined}
                onPress={() => {
                  if (!selectedVisit) {
                    return;
                  }
                  const { patientId, visitId, medicalExams, status } =
                    selectedVisit;

                  // Preferred name / consent are the QR/manual flow's job,
                  // never re-shown here. Active Sessions only continues a
                  // visit into room assignment (not yet Ongoing) or straight
                  // into its scan/follow-up step (already Ongoing/Completed).
                  const resumeUrl =
                    status === APITypesV1.VisitStatus.Completed
                      ? paths.checkOut.url({ patientId, visitId })
                      : status === APITypesV1.VisitStatus.Ongoing
                        ? getScanFlowUrl({ patientId, visitId, medicalExams })
                        : paths.assignToRoom.url({ patientId, visitId });

                  if (featureFlags.newCheckInFlowEnabled) {
                    // Resuming here is a real handover: a different device
                    // or operator may be picking the visit back up, so
                    // identity gets reconfirmed, then the immediate risk
                    // assessment is asked here instead of during check-in,
                    // both right before continuing.
                    const riskAssessmentUrl = `${paths.immediateRiskAssessment.url(
                      { patientId, visitId }
                    )}?returnPath=${encodeURIComponent(resumeUrl)}`;
                    const identityCheckUrl = paths.identityCheck.url({
                      patientId,
                      visitId,
                    });
                    goTo(
                      `${identityCheckUrl}?returnPath=${encodeURIComponent(riskAssessmentUrl)}`
                    );
                  } else {
                    goTo(
                      `${paths.selectVisitConfirm.url({ patientId, visitId })}?returnPath=${encodeURIComponent(resumeUrl)}`
                    );
                  }
                }}
                fillContainer
                icon={<Icon24ArrowRight />}
              >
                Continue
              </Button>
            }
          />
        }
      />
    </PageFrameContent>
  );
}

function VisitRadio({ visit, rooms }: { visit: Visit; rooms: Room[] }) {
  const patientId = visit.patientId;
  const patientQuery = usePatientQuery({ patientId });
  const visitHistoryQuery = useVisitHistoryQuery({ patientId });
  const visitTypeLabel =
    visitHistoryQuery.data != null
      ? generateVisitTypeLabel({ visit, visitHistory: visitHistoryQuery.data })
      : undefined;

  const visitRoom = rooms.find((room) => room.id === visit.roomId);
  const fullName = `${patientQuery.data?.name?.firstName} ${patientQuery.data?.name?.lastName}`;
  const time = `${visit?.startTime.toFormat(
    "HH:mm"
  )} - ${visit?.endTime.toFormat("HH:mm")}`;

  return (
    <RadioGroup.Radio
      key={visit.visitId}
      value={visit?.visitId}
      label={fullName}
      bottomLeft={
        <Typography variant="body-m" as="span" color="subtle">
          {visitTypeLabel}
        </Typography>
      }
      topRight={visitRoom?.displayName}
      bottomRight={
        <Typography variant="body-m" as="span" color="subtle">
          {time}
        </Typography>
      }
    />
  );
}
